import './index.less';

import { Table, TableProps as AntdTableProps } from 'antd';
import { ColumnType, ColumnsType } from 'antd/lib/table/interface';
import classNames from 'classnames';

import moment from 'moment';
import { TreeShaped } from 'domains/appDomain';
import { MinusSquareTwoTone, PlusSquareTwoTone } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { RevxLoader } from 'components/RevxLoader';
export interface TableColumnType<T> extends ColumnType<T> {
  valueType?: 'money' | 'time' | 'string' | 'number' | 'percentage';
  dateFormat?: 'string';
  decimalDigits?: number;
}

export interface TableColumnGroupType<T>
  extends Omit<TableColumnType<T>, 'dataIndex'> {
  children: TableColumnsType<T>;
}

export declare type TableColumnsType<T> = (
  | TableColumnGroupType<T>
  | TableColumnType<T>
)[];

export interface RevxTableProps<T> extends AntdTableProps<T> {
  width?: number | string;
  minWidth?: number | string;
  scrollWidth?: number | string;
}

function cellRender<T>(
  column: TableColumnType<T>,
  value: any,
  record: T,
  index: number
) {
  if (value === null || value === undefined) {
    return '';
  }
  let valueType = column.valueType || 'string';
  let formattedValue = value;
  switch (valueType) {
    case 'money':
      formattedValue = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: column.decimalDigits || 2,
      }).format(value);
      break;
    case 'time':
      formattedValue = moment(value).format(column.dateFormat || 'YYYY-MM-DD');
      break;
    case 'percentage':
      if (value === null || value === undefined) {
        formattedValue = '';
      } else {
        formattedValue = (value || 0) + '%';
      }
      break;

    default:
      break;
  }
  return formattedValue;
}

function isTableColumnGroupType<T>(
  value: TableColumnGroupType<T> | TableColumnType<T>
): value is TableColumnGroupType<T> {
  return value.hasOwnProperty('children');
}

export function RevxTable<T extends object>(props: RevxTableProps<T>) {
  const antdColumns: ColumnsType<T> = [];
  const { t } = useTranslation();

  props.columns?.forEach((column) => {
    let col;
    if (isTableColumnGroupType(column)) {
      col = {
        ...column,
        children: column.children.map((col) => {
          return {
            ...col,
            render:
              col.render ||
              function (value: any, record: T, index: number) {
                return cellRender(col, value, record, index);
              },
          };
        }),
      };
    } else {
      col = {
        ...column,
        render:
          column.render ||
          function (value: any, record: T, index: number) {
            return cellRender(column, value, record, index);
          },
      };
    }

    antdColumns.push(col);
  });

  return (
    <Table<T>
      {...props}
      pagination={
        props.pagination
          ? {
              showTotal: (total) => t('common.pagerTotal', { total: total }),
              ...props.pagination,
            }
          : props.pagination === false
          ? false
          : {
              showTotal: (total) => t('common.pagerTotal', { total: total }),
            }
      }
      className={classNames('revx-table', props.className)}
      columns={antdColumns}
      rowKey={props.rowKey || 'id'}
      style={{
        width: props.width || 'auto',
        minWidth: props.minWidth || 0,
      }}
      expandable={{
        expandIcon: ({ expanded, onExpand, record }) => {
          if (
            (record as TreeShaped<T>).children ||
            props.expandable?.expandedRowRender
          ) {
            return expanded ? (
              <MinusSquareTwoTone
                style={{ fontSize: 17, cursor: 'pointer' }}
                onClick={(e) => {
                  if (props.expandable?.expandedRowRender) {
                    onExpand(record, e);
                  }
                }}
              />
            ) : (
              <PlusSquareTwoTone
                style={{ fontSize: 17, cursor: 'pointer' }}
                onClick={(e) => {
                  if (props.expandable?.expandedRowRender) {
                    onExpand(record, e);
                  }
                }}
              />
            );
          } else {
            return null;
          }
        },
        ...props.expandable,
      }}
      loading={{
        spinning: (props.loading as boolean) || false,
        indicator: <RevxLoader loading />,
      }}
      scroll={
        props.scrollWidth
          ? {
              x: props.scrollWidth,
            }
          : {
              ...props.scroll,
            }
      }
    />
  );
}

export default RevxTable;
